import React, { useState, useContext, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import apiClient from '../../utils/apiClient'
import ebrLogo from '../../assets/icons/ebrLogo.svg'
import rCircle from '../../assets/icons/rCircle.svg'
import ConfirmationModal from '../../components/UI/ConfirmationModal'
import './OTPPage.css'
import TokenService from '../../utils/tokenService'
import { ProfileContext } from '../../contexts/ProfileContext'


const MAX_INCORRECT_ATTEMPTS = 5
const LOCKOUT_DURATION_MS = 5 * 60 * 1000 
const STORAGE_ATTEMPTS_KEY = 'otpIncorrectAttempts'
const STORAGE_BLOCK_KEY = 'otpBlockUntil'

const OTPPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshProfile } = useContext(ProfileContext)

  const maskedNumber = location.state?.phone || '+1 *** *** ***'
  const phoneNumber = location.state?.phoneNumber || ''
  const [session, setSession] = useState(location.state?.session || '')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [timeLeftStr, setTimeLeftStr] = useState('')
  const [incorrectAttempts, setIncorrectAttempts] = useState(0)
  const [blockUntil, setBlockUntil] = useState(null)

  const inputRefs = [
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null),
      useRef(null)
    ];

  useEffect(() => {
    const storedAttempts = parseInt(localStorage.getItem(STORAGE_ATTEMPTS_KEY)) || 0
    setIncorrectAttempts(storedAttempts)

    const storedBlock = parseInt(localStorage.getItem(STORAGE_BLOCK_KEY), 10)
    if (storedBlock && storedBlock > Date.now()) {
      setBlockUntil(storedBlock)
    } else {
      localStorage.removeItem(STORAGE_BLOCK_KEY)
      if (storedAttempts >= MAX_INCORRECT_ATTEMPTS) {
        localStorage.removeItem(STORAGE_ATTEMPTS_KEY)
        setIncorrectAttempts(0)
      }
    }
  }, [])

  useEffect(() => {
    if (!blockUntil){
      setTimeLeftStr('')
      return
    } 

    const updateTimeLeft = () => {
      const now = Date.now()
      const msLeft = blockUntil - now
      if (msLeft <= 0) {
        setTimeLeftStr('')
        clearBlock()
        return
      }
      const minutes = Math.floor(msLeft / 60000)
      const seconds = Math.floor((msLeft % 60000) / 1000)
      const paddedSec = seconds < 10 ? '0' + seconds : seconds
      setTimeLeftStr(`${minutes}:${paddedSec}`)
    }

    updateTimeLeft()

    const timerId = setInterval(() => {
      updateTimeLeft()
    }, 1000)

    return () => clearInterval(timerId)
  }, [blockUntil])


  const clearBlock = () => {
    setBlockUntil(null)
    setIncorrectAttempts(0)
    localStorage.removeItem(STORAGE_BLOCK_KEY)
    localStorage.removeItem(STORAGE_ATTEMPTS_KEY)
    setTimeLeftStr('')
  }

  useEffect(() => {
    if (!session || !phoneNumber) navigate('/')
  }, [session, phoneNumber, navigate])

  const isLocked = blockUntil && blockUntil > Date.now()

  const handleChange = (i, v) => {
    const c = v.replace(/\D/g, '')
    if (!c && v !== '') return
    const next = [...otp]
    next[i] = c.slice(-1)
    setOtp(next)
    if (i < 5 && c) inputRefs[i + 1].current.focus()
  }

  const handleKeyDown = (e, i) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      inputRefs[i - 1].current.focus()
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (isLocked) {
      const minutesLeft = Math.ceil((blockUntil - Date.now()) / 60000)
      setModalMessage(`Too many incorrect attempts. Please wait ${minutesLeft} minute(s).`)
      setIsModalOpen(true)
      return
    }

    setIsVerifying(true)
    const code = otp.join('')
    if (code.length !== 6) {
      setModalMessage('Please enter a valid 6-digit OTP')
      setIsModalOpen(true)
      setIsVerifying(false)
      return
    }

    try {
      const payload = {
        PhoneNumber: phoneNumber,
        Session: session,
        Otp: code,
      }
      const { data } = await apiClient.post('/auth/verify', payload)

      if (data.Session) setSession(data.Session)
      if (data.AccessToken && data.RefreshToken && data.IdToken) {
        TokenService.setAccessToken(data.AccessToken)
        TokenService.setRefreshToken(data.RefreshToken)
        TokenService.setIdToken(data.IdToken)
        clearBlock()
        try {
          await refreshProfile()
        } catch (err) {
          console.error('Failed to load profile after login:', err)
        }
        navigate('/dashboard')
        return
      }
      throw new Error('NoTokens')
    } catch (err) {
      const status = err.response?.status
      const errorCode = err.response?.data?.code || err.code

      if (
        status === 500 ||
        errorCode === 'CodeMismatchException' ||
        errorCode === 'NotAuthorizedException' ||
        err.message === 'NoTokens'
      ) {
        const nextCount = incorrectAttempts + 1
        setIncorrectAttempts(nextCount)
        localStorage.setItem(STORAGE_ATTEMPTS_KEY, String(nextCount))

        if (nextCount >= MAX_INCORRECT_ATTEMPTS) {
          const unblockTime = Date.now() + LOCKOUT_DURATION_MS
          setBlockUntil(unblockTime)
          localStorage.setItem(STORAGE_BLOCK_KEY, String(unblockTime))
          setModalMessage('Too many incorrect attempts. Please wait 5 minutes before trying again.')
        } else {
          try {
            const resp = await apiClient.post('/auth/admin_signin', { PhoneNumber: phoneNumber })
            setSession(resp.data)
            setModalMessage(
              `Invalid OTP. A new code has been sent. ` +
              `You have ${MAX_INCORRECT_ATTEMPTS - nextCount} attempt(s) left.`
            )
          } catch {
            setModalMessage('Invalid OTP and failed to resend. Please try again later.')
          }
        }

        setIsModalOpen(true)
        setOtp(['', '', '', '', '', ''])
        inputRefs[0].current.focus()
        setIsVerifying(false)
      } else {
        setModalMessage('Server error. Please try again later.')
        setIsVerifying(false)
      }
    }
  }

  const handleRetry = async () => {
    setIsResending(true)
    try {
      const resp = await apiClient.post('/auth/admin_signin', { PhoneNumber: phoneNumber })
      setSession(resp.data)
      setModalMessage('A new OTP has been sent. Please enter the new code.')
      setIsModalOpen(true)
      setOtp(['', '', '', '', '', ''])
      inputRefs[0].current.focus()
    } catch {
      setModalMessage('Server error. Please try again later.')
      setIsModalOpen(true)
    } finally {
      setIsResending(false)
    }
  }

  const handleModalConfirm = () => {
    setIsModalOpen(false)
  }

  return (
    <div className="login-page-container">
      <div className="login-page-left">
        <div className="ebr-logo-container">
          <img src={ebrLogo} alt="Eberhard Logo" className="ebr-logo" />
        </div>
        <h1 className="login-hero-title">Welcome Back</h1>
        <p className="login-hero-sub">
          Simplify your experience with a single click. Your workspace, your way.
        </p>
      </div>
      <div className="login-page-right">
        <h2 className="otp-title">Enter OTP</h2>
        <p className="otp-subtext">
          Please enter the OTP sent to <strong>{maskedNumber}</strong>
        </p>

        <form className="otp-form" onSubmit={handleSubmit} data-testid="otp-form">
          <div className="otp-inputs">
            {otp.map((d, i) => (
              <input
                key={i}
                type="text"
                className="otp-input"
                maxLength="1"
                ref={inputRefs[i]}
                value={d}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(e, i)}
                autoFocus={i === 0}
              />
            ))}
          </div>
          <button
            className="otp-submit-button"
            type="submit"
            disabled={isVerifying || isLocked}
          >
            {isVerifying
              ? 'Verifying...'
              : isLocked
                ? 'Locked (${timeLeftStr})'
                : 'Verify OTP'}
          </button>
        </form>

        <div className="otp-actions">
          <button className="otp-back-button" onClick={() => navigate('/')}>
            &larr; Go Back
          </button>
          <button
            className="otp-retry-button"
            onClick={handleRetry}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Resend OTP'}
          </button>
        </div>

        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={handleModalConfirm}
          message={modalMessage}
          confirmLabel="OK"
          showCancel={false}
        />
      </div>
    </div>
  )
}

export default OTPPage
